import { useMemo } from 'react'

import duneData from './duneData.json'
import { FARM_ADDRESSES, FarmStaker, LPHolder, PairData, SunsetData } from './types'

export * from './types'

// Load sunset data from static JSON
export function useSunsetData(): SunsetData {
  return useMemo(
    () => ({
      pairs: duneData.pairs as PairData[],
      holders: duneData.holders as LPHolder[],
      farmStakers: duneData.farmStakers as FarmStaker[],
      lastUpdated: duneData.lastUpdated
    }),
    []
  )
}

// Get total TVL across all pairs
export function useTotalTVL(): number {
  const { pairs } = useSunsetData()
  return useMemo(() => {
    return pairs.reduce((sum, pair) => sum + pair.tvlUsd, 0)
  }, [pairs])
}

// Get total number of unique holders
export function useTotalHolders(): number {
  const { holders } = useSunsetData()
  return useMemo(() => {
    const uniqueAddresses = new Set(holders.map(h => h.userAddress.toLowerCase()))
    return uniqueAddresses.size
  }, [holders])
}

// Get holders for a specific address (case-insensitive)
export function useHolderPositions(address: string | undefined): LPHolder[] {
  const { holders } = useSunsetData()
  return useMemo(() => {
    if (!address) return []
    const lowerAddress = address.toLowerCase()
    return holders.filter(h => h.userAddress.toLowerCase() === lowerAddress)
  }, [holders, address])
}

// Get TVL staked in farms (LP held by farm contracts)
export function useFarmTVL(): number {
  const { holders } = useSunsetData()
  return useMemo(() => {
    const farmAddresses = Object.values(FARM_ADDRESSES).map(a => a.toLowerCase())
    return holders
      .filter(h => farmAddresses.includes(h.userAddress.toLowerCase()))
      .reduce((sum, h) => sum + h.balanceUsd, 0)
  }, [holders])
}

// Check if an address is a farm contract
export function isFarmContract(address: string): boolean {
  const lowerAddress = address.toLowerCase()
  return Object.values(FARM_ADDRESSES).some(farmAddr => farmAddr.toLowerCase() === lowerAddress)
}

// Get pairs grouped by chain
export function usePairsByChain(): Record<string, PairData[]> {
  const { pairs } = useSunsetData()
  return useMemo(() => {
    return pairs.reduce((acc, pair) => {
      if (!acc[pair.chain]) acc[pair.chain] = []
      acc[pair.chain].push(pair)
      return acc
    }, {} as Record<string, PairData[]>)
  }, [pairs])
}

// Get holders grouped by chain
export function useHoldersByChain(): Record<string, LPHolder[]> {
  const { holders } = useSunsetData()
  return useMemo(() => {
    return holders.reduce((acc, holder) => {
      if (!acc[holder.chain]) acc[holder.chain] = []
      acc[holder.chain].push(holder)
      return acc
    }, {} as Record<string, LPHolder[]>)
  }, [holders])
}

// Get all farm stakers
export function useFarmStakers(): FarmStaker[] {
  const { farmStakers } = useSunsetData()
  return farmStakers
}

// Get total number of unique farm stakers
export function useTotalFarmStakers(): number {
  const { farmStakers } = useSunsetData()
  return useMemo(() => {
    const uniqueAddresses = new Set(farmStakers.map(s => s.userAddress.toLowerCase()))
    return uniqueAddresses.size
  }, [farmStakers])
}

// Get farm stakes for a specific address (case-insensitive)
export function useFarmStakerPositions(address: string | undefined): FarmStaker[] {
  const { farmStakers } = useSunsetData()
  return useMemo(() => {
    if (!address) return []
    const lowerAddress = address.toLowerCase()
    return farmStakers.filter(s => s.userAddress.toLowerCase() === lowerAddress)
  }, [farmStakers, address])
}

// Get farm stakers grouped by chain
export function useFarmStakersByChain(): Record<string, FarmStaker[]> {
  const { farmStakers } = useSunsetData()
  return useMemo(() => {
    return farmStakers.reduce((acc, staker) => {
      if (!acc[staker.chain]) acc[staker.chain] = []
      acc[staker.chain].push(staker)
      return acc
    }, {} as Record<string, FarmStaker[]>)
  }, [farmStakers])
}

// Format USD value
export function formatUsd(value: number): string {
  if (value < 0.01) return '< $0.01'
  if (value < 1) return `$${value.toFixed(2)}`
  if (value < 1000) return `$${value.toFixed(2)}`
  if (value < 1000000) return `$${(value / 1000).toFixed(2)}K`
  return `$${(value / 1000000).toFixed(2)}M`
}

// Shorten address for display
export function shortenAddress(address: string, chars = 4): string {
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`
}

// Get block explorer URL
export function getExplorerUrl(
  chainId: number,
  addressOrHash: string,
  type: 'address' | 'token' | 'tx' = 'address'
): string {
  const explorers: Record<number, string> = {
    137: 'https://polygonscan.com',
    56: 'https://bscscan.com',
    42161: 'https://arbiscan.io',
    1: 'https://etherscan.io'
  }
  const base = explorers[chainId] || explorers[137]
  return `${base}/${type}/${addressOrHash}`
}
